import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axiosInstance from "../../../interceptors";
import { baseUrl } from "../../../config/config";

const isoOf = (date, time) => {
  if (!date || !time) return null;
  return `${date}T${time}:00`;
};

export default function SchedulingCreate() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    candidateId: params.get("candidateId") || "",
    jobId: params.get("jobId") || "",
    durationMinutes: 45,
    location: "Online",
    meetingLink: "",
    instructions: "",
  });
  const [slots, setSlots] = useState([{ date: "", startTime: "", endTime: "" }]);
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);

  const updateSlot = (i, patch) => setSlots((s) => s.map((sl, idx) => idx === i ? { ...sl, ...patch } : sl));
  const removeSlot = (i) => setSlots((s) => s.filter((_, idx) => idx !== i));
  const addSlot = () => setSlots([...slots, { date: "", startTime: "", endTime: "" }]);

  const create = async () => {
    if (!form.candidateId || !form.jobId) { alert("candidateId + jobId required"); return; }
    const slotJson = slots
      .map((s) => ({ start: isoOf(s.date, s.startTime), end: isoOf(s.date, s.endTime) }))
      .filter((s) => s.start && s.end);
    if (slotJson.length === 0) { alert("Add at least one slot"); return; }
    setBusy(true);
    try {
      const r = await axiosInstance.post(`${baseUrl}/job-posting/scheduling/create-link`, {
        candidateId: Number(form.candidateId),
        jobId: Number(form.jobId),
        durationMinutes: Number(form.durationMinutes),
        location: form.location,
        meetingLink: form.meetingLink || null,
        instructions: form.instructions,
        availableSlots: JSON.stringify(slotJson),
      });
      setDone(r.data);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="page-enter max-w-2xl mx-auto py-12 text-center">
        <div className="text-5xl mb-3">🔗</div>
        <h2 className="text-xl font-semibold text-vf-base">Scheduling link created</h2>
        <p className="text-sm text-vf-muted mt-2">Send this link to the candidate:</p>
        <div className="mt-3 px-4 py-2 bg-slate-100 rounded inline-block font-mono text-sm">
          {window.location.origin}{done.candidateLink}
        </div>
        <div className="mt-5">
          <button onClick={() => nav("/admin/scheduling-bookings")} className="px-4 py-2 rounded bg-vf-primary text-white text-sm">
            View all bookings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-enter">
      <div className="mb-5">
        <button onClick={() => nav(-1)} className="text-xs text-vf-muted hover:text-vf-base mb-1">← Back</button>
        <h1 className="text-2xl font-semibold text-vf-base">Create scheduling link</h1>
        <p className="text-sm text-vf-muted mt-1">Manually pick available slots for the candidate to choose from.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3 mb-4">
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <div className="text-xs font-medium text-vf-muted mb-0.5">Candidate ID *</div>
            <input value={form.candidateId} onChange={(e) => setForm({...form, candidateId: e.target.value})} className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm" />
          </label>
          <label className="block">
            <div className="text-xs font-medium text-vf-muted mb-0.5">Job ID *</div>
            <input value={form.jobId} onChange={(e) => setForm({...form, jobId: e.target.value})} className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm" />
          </label>
          <label className="block">
            <div className="text-xs font-medium text-vf-muted mb-0.5">Duration (min)</div>
            <input type="number" value={form.durationMinutes} onChange={(e) => setForm({...form, durationMinutes: e.target.value})} className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm" />
          </label>
          <label className="block">
            <div className="text-xs font-medium text-vf-muted mb-0.5">Location</div>
            <input value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm" />
          </label>
        </div>
        <label className="block">
          <div className="text-xs font-medium text-vf-muted mb-0.5">Meeting link</div>
          <input value={form.meetingLink} onChange={(e) => setForm({...form, meetingLink: e.target.value})} placeholder="https://meet.google.com/..." className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm" />
        </label>
        <label className="block">
          <div className="text-xs font-medium text-vf-muted mb-0.5">Instructions</div>
          <textarea value={form.instructions} onChange={(e) => setForm({...form, instructions: e.target.value})} rows={2} className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm resize-none" />
        </label>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="text-sm font-medium text-vf-base mb-3">Available time slots</div>
        <div className="space-y-2">
          {slots.map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <input type="date" value={s.date} onChange={(e) => updateSlot(i, { date: e.target.value })} className="px-2 py-1.5 border border-slate-300 rounded text-sm" />
              <input type="time" value={s.startTime} onChange={(e) => updateSlot(i, { startTime: e.target.value })} className="px-2 py-1.5 border border-slate-300 rounded text-sm" />
              <span className="text-vf-muted text-xs">to</span>
              <input type="time" value={s.endTime} onChange={(e) => updateSlot(i, { endTime: e.target.value })} className="px-2 py-1.5 border border-slate-300 rounded text-sm" />
              <button onClick={() => removeSlot(i)} className="ml-2 text-xs text-red-600">Remove</button>
            </div>
          ))}
        </div>
        <button onClick={addSlot} className="mt-3 px-3 py-1.5 rounded border border-dashed border-slate-300 text-xs text-vf-muted">
          + Add slot
        </button>
      </div>

      <div className="flex justify-end mt-4">
        <button onClick={create} disabled={busy} className="px-4 py-2 rounded bg-vf-primary text-white text-sm">
          {busy ? "Creating…" : "Create link"}
        </button>
      </div>
    </div>
  );
}
